// Bottle specs (W0's app/frontend/assets/bottles/bottles.json) and runtime
// image assets. Module scope only holds data + URL strings (Vite resolves the
// PNG imports to URLs in both the client and the SSR bundle) — no Image or
// canvas is touched until a function is called in the browser.

import bottlesJson from "@/assets/bottles/bottles.json";
import wineUrl from "@/assets/bottles/wine.png";
import mccUrl from "@/assets/bottles/mcc.png";
import type { BottleType } from "./schema";

/** One bottles.json entry. All values are pixels of the cropped bottle PNG. */
export interface BottleSpec {
  image: { width: number; height: number };
  /** Bottle scale (≈30 cm bottle height). Informational — see labelScale(). */
  px_per_cm: number;
  /** Where the true-proportion front label sits on the bottle image. */
  label: { x: number; y: number; width: number; height: number };
}

export type BottlesJson = Record<BottleType, BottleSpec>;

/** The bundled bottles.json (identical to the `bottles` prop the studio page receives). */
export const BOTTLES: BottlesJson = bottlesJson as BottlesJson;

/** Bundled bottle PNG URLs. */
export const BOTTLE_IMAGE_URLS: Readonly<Record<BottleType, string>> = {
  wine: wineUrl,
  mcc: mccUrl,
};

/** Anything canvas drawImage accepts that we use. */
export type ImageSource = HTMLImageElement | ImageBitmap | HTMLCanvasElement;

/**
 * Runtime images a render needs.
 * - `uploadedImage`: the decoded single upload (the ImageElement's `src: "uploaded"`).
 *   Missing/undefined → the image element is skipped in exports and shown as a
 *   dashed placeholder in the editor.
 * - `bottleImages`: decoded bottle PNGs; only the doc's bottle is needed.
 */
export interface Assets {
  uploadedImage?: ImageSource | null;
  bottleImages: Partial<Record<BottleType, ImageSource>>;
}

/**
 * Label scale on the bottle image, px per mm, derived from the bottles.json
 * label rectangle and the label's mm size. Both axes are returned; W0 built the
 * rectangle at true proportion so they agree to < 0.2 %. NOTE: this is smaller
 * than px_per_cm / 10 — by locked decision 2 the label is scaled so its width
 * equals the VISIBLE bottle width at the patch (a flat composite of a
 * cylinder), not the bottle's 30 cm height scale.
 */
export function labelScale(
  spec: BottleSpec,
  labelMm: { width_mm: number; height_mm: number },
): { x: number; y: number } {
  return {
    x: spec.label.width / labelMm.width_mm,
    y: spec.label.height / labelMm.height_mm,
  };
}

/** Decode an image URL (object URL, same-origin path or data URL). Browser only. */
export function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    if (typeof Image === "undefined") {
      reject(new Error("loadImage is browser-only"));
      return;
    }
    const img = new Image();
    img.decoding = "async";
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error(`Could not load image: ${src.slice(0, 80)}`));
    img.src = src;
  });
}

/** Decode both bottle PNGs. Browser only; resolves with whichever loaded. */
export async function loadBottleImages(): Promise<Assets["bottleImages"]> {
  const entries = await Promise.all(
    (Object.keys(BOTTLE_IMAGE_URLS) as BottleType[]).map(async (k) => {
      try {
        return [k, await loadImage(BOTTLE_IMAGE_URLS[k])] as const;
      } catch {
        return [k, undefined] as const;
      }
    }),
  );
  const out: Assets["bottleImages"] = {};
  for (const [k, img] of entries) if (img) out[k] = img;
  return out;
}

/** Natural pixel size of any ImageSource. */
export function naturalSize(img: ImageSource): { width: number; height: number } {
  if ("naturalWidth" in img) return { width: img.naturalWidth, height: img.naturalHeight };
  return { width: img.width, height: img.height };
}
